import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightQueriesPreviewResults, InsightQuery } from 'dashboard/models/insight-queries';

interface InsightQueriesState {
  queries: InsightQuery[];
  activeQuery: InsightQuery | null;
  results: InsightQueriesPreviewResults;
  error: string | null;
  expandedTablesAvailable: boolean;
}

interface SetInsightQueryResultsPayload {
  results: InsightQueriesPreviewResults;
  error: string | null;
}

const initialState: InsightQueriesState = {
  queries: [],
  activeQuery: null,
  results: [],
  error: null,
  expandedTablesAvailable: false,
};

const { reducer, actions } = createSlice({
  name: 'insight-query',
  initialState,
  reducers: {
    archiveInsightQuery(state, { payload: { id } }: PayloadAction<{ id: string }>) {
      state.queries = state.queries.filter((query) => query.id !== id);
    },
    updateInsightQuery(state, { payload: { query } }: PayloadAction<{ query: InsightQuery }>) {
      state.queries = state.queries.map((q) => (q.id === query.id ? query : q));
      state.activeQuery = query;
    },
    clearInsightQueriesResults(state) {
      state.results = [];
      state.activeQuery = null;
    },
    setNewInsightQuery(state, { payload: { query } }: PayloadAction<{ query: InsightQuery }>) {
      state.queries = [...state.queries, query];
      state.activeQuery = query;
    },
    setActiveQueryByToken(state, { payload: { token } }: PayloadAction<{ token: string }>) {
      const query = state.queries.find((q) => q.id === token);
      if (query) {
        state.activeQuery = query;
      }
    },
    setInsightQueries(state, { payload: { queries } }: PayloadAction<{ queries: InsightQuery[] }>) {
      state.queries = queries;
    },
    setInsightQueryResults(
      state,
      { payload: { results, error } }: PayloadAction<SetInsightQueryResultsPayload>,
    ) {
      state.results = results;
      state.error = error ? error : null;
    },
    setExpandedTablesAvailable(
      state,
      { payload: { expandedTableExists } }: PayloadAction<{ expandedTableExists: boolean }>,
    ) {
      state.expandedTablesAvailable = expandedTableExists;
    },
  },
});

export const {
  archiveInsightQuery,
  setActiveQueryByToken,
  clearInsightQueriesResults,
  setInsightQueries,
  setInsightQueryResults,
  setNewInsightQuery,
  setExpandedTablesAvailable,
  updateInsightQuery,
} = actions;

export const InsightQueriesReducer = reducer;
